import { motion } from 'framer-motion';
import MarketingLayout from '../components/MarketingLayout';
import SEO from '../components/SEO';

const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

export default function PrivacyPolicyPage() {
  return (
    <MarketingLayout>
      <SEO
        title="Privacy Policy"
        description="PAFHub Privacy Policy — how we collect, use, and protect your personal and compliance data."
        path="/privacy"
      />
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 bg-gradient-to-br from-indigo-50/80 via-white to-sky-50/50">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <p className="text-sm font-semibold text-indigo-600 uppercase tracking-wider mb-3">Legal</p>
            <h1 className="text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight mb-4">Privacy Policy</h1>
            <p className="text-[#64748B]">Last updated: April 1, 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 prose prose-slate prose-headings:text-[#0F172A] prose-headings:font-bold prose-p:text-[#475569] prose-p:leading-[1.8] prose-li:text-[#475569] prose-strong:text-[#0F172A] max-w-none">

          <h2>1. Introduction</h2>
          <p>PAFHub ("we," "our," or "us") operates the PAFHub platform at pafhub.com. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services. We are committed to protecting the privacy of our users, particularly given the sensitive nature of immigration compliance data.</p>

          <h2>2. Information We Collect</h2>
          <h3>2.1 Account Information</h3>
          <p>When you create an account, we collect:</p>
          <ul>
            <li>First and last name</li>
            <li>Email address</li>
            <li>Password (stored in hashed form only)</li>
            <li>Company name, EIN, address, and phone number (when provided)</li>
          </ul>

          <h3>2.2 Employee & Compliance Data</h3>
          <p>To provide our PAF management services, you may upload:</p>
          <ul>
            <li>Employee names, email addresses, job titles, and visa information (H-1B, H-1B1, E-3)</li>
            <li>LCA case numbers, filing dates, expiration dates, wage levels, and worksite addresses</li>
            <li>Documents including LCA copies, wage memos, posting notices, and other PAF-required materials</li>
          </ul>

          <h3>2.3 Payment Information</h3>
          <p>Payment processing is handled by Stripe. We do not store your credit card numbers or bank account details on our servers. Stripe's privacy policy governs the collection and use of payment data.</p>

          <h3>2.4 Usage Data</h3>
          <p>We automatically collect certain information when you use our platform, including IP address, browser type, pages visited, time spent, and actions taken within the application.</p>

          <h2>3. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, operate, and maintain the PAFHub platform</li>
            <li>Process PAF documents and generate compliance packets</li>
            <li>Send compliance reminder emails and notifications you have opted into</li>
            <li>Process subscription payments and manage your account</li>
            <li>Send account verification emails and password reset codes</li>
            <li>Improve our platform based on usage patterns</li>
            <li>Respond to support inquiries and customer service requests</li>
            <li>Comply with legal obligations</li>
          </ul>

          <h2>4. Data Isolation & Multi-Tenancy</h2>
          <p>PAFHub enforces strict per-account data isolation. Each user's employees, PAF records, documents, and audit logs are scoped to their account and cannot be accessed by other users. We use technical controls at the database level to ensure this separation.</p>

          <h2>5. Data Sharing & Disclosure</h2>
          <p>We do not sell your personal information. We may share data with:</p>
          <ul>
            <li><strong>Service Providers:</strong> Stripe (payments), Resend (transactional emails), and cloud infrastructure providers necessary to operate the platform</li>
            <li><strong>Public Access File Sharing:</strong> When you generate a public access link for a PAF, the information in that PAF becomes accessible to anyone with the link. You control which PAFs have public links enabled</li>
            <li><strong>Legal Requirements:</strong> We may disclose information if required by law, subpoena, or government request</li>
          </ul>

          <h2>6. Data Security</h2>
          <p>We implement industry-standard security measures including:</p>
          <ul>
            <li>Encryption of data in transit (TLS/SSL)</li>
            <li>Hashed passwords using bcrypt</li>
            <li>JWT-based authentication with expiring tokens</li>
            <li>Secure, HTTP-only session cookies</li>
            <li>Regular security reviews and monitoring</li>
          </ul>

          <h2>7. Data Retention</h2>
          <p>We retain your data for as long as your account is active. If you delete your account through the Settings page, we permanently remove all associated data including employee records, PAF files, uploaded documents, and audit logs. This action is irreversible.</p>

          <h2>8. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access the personal data we hold about you</li>
            <li>Correct inaccurate information in your account</li>
            <li>Delete your account and all associated data</li>
            <li>Export your data</li>
            <li>Opt out of non-essential communications</li>
          </ul>

          <h2>9. Children's Privacy</h2>
          <p>PAFHub is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children.</p>

          <h2>10. Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page and updating the "Last updated" date.</p>

          <h2>11. Contact Us</h2>
          <p>If you have questions about this Privacy Policy or our data practices, please contact us at:</p>
          <ul>
            <li>Email: compliance@pafhub.com</li>
            <li>Website: pafhub.com/contact</li>
          </ul>
        </div>
      </section>
    </MarketingLayout>
  );
}
